import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { navSelector } from '../../../../store/js/modules/competition-nav/selectors/nav-selector';
import { filtersSelector } from '../../../../store/js/modules/competition-nav/selectors/filters-selector';
import { useGetMatchesQuery } from '../../../../store/js/modules/api/afl-api';
import { groupMatchesByDate } from '../../utils/group-matches-by-date';
import { getGroupKeys } from '../../utils/get-group-keys';
import { filterByCity } from '../../utils/filter-by-city';

import { EmptyState } from '../../../../../../common/react/components/EmptyState';

import { FixturesGroup } from './FixturesGroup';
import { ByesGroup } from './ByesGroup';
import { FixturesSkeleton } from './FixturesSkeleton';

import { getTranslation } from '../../../../../../common/react/utils/translations';

const LAST_BYE_ROUND = 23;

const checkIfBroadcasterGuide = (providerId) => {
    const translationString = getTranslation(
        `label.broadcastGuideURL.${providerId}`
    );

    if (translationString !== `label.broadcastGuideURL.${providerId}`) {
        return translationString;
    }

    return false;
};

export const FixturesList = () => {
    const nav = useSelector(navSelector);
    const filters = useSelector(filtersSelector);

    const broadcastGuideUrl =
        checkIfBroadcasterGuide(nav?.season.providerId) ?? '';

    const { matches = [], isMatchesFetching } = useGetMatchesQuery(
        {
            competitionId: nav.competition.id,
            compSeasonId: nav.season.id,
            roundNumber: nav.round.roundNumber,
            ...(filters.teams.length && { teamId: filters.teams }),
            ...(filters.venues.length && { venueId: filters.venues })
        },
        {
            selectFromResult: ({ data, isFetching }) => ({
                matches: filters.cities.length
                    ? filterByCity(data, filters.cities)
                    : data,
                isMatchesFetching: isFetching
            })
        }
    );

    const groups = useMemo(() => {
        // Group results based on start day/month
        if (matches) {
            return groupMatchesByDate(matches);
        }
    }, [matches]);

    const groupKeys = useMemo(() => {
        if (groups) {
            return getGroupKeys(groups);
        }
    }, [groups]);
    const roundNumber = nav?.round?.roundNumber;

    if (isMatchesFetching) {
        return <FixturesSkeleton />;
    }

    if (!matches || !matches.length) {
        return <EmptyState cssClass="competition-nav__empty-state" />;
    }

    return (
        <>
            {groupKeys && groups
                ? groupKeys.map((dateKey) => (
                      <FixturesGroup
                          key={dateKey}
                          dateKey={dateKey}
                          group={groups[dateKey]}
                          broadcastGuideUrl={broadcastGuideUrl}
                      />
                  ))
                : null}

            {roundNumber && roundNumber < LAST_BYE_ROUND && nav?.season.id && (
                <ByesGroup
                    compseasonId={nav.season.id}
                    roundNumber={roundNumber}
                />
            )}
        </>
    );
};
